import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, metaForPath, isPrivatePath } from './routes';

// Rewrites the document head on every route change.
//
// The head is baked once into public/index.html at build time and react
// never touched it again, so all ~30 routes shipped the same title, the same
// description and no canonical. Search engines saw one page.
//
// No react-helmet: this needs to run for every route, it is the only thing
// writing the head, and a dependency that manages a stack of nested heads
// buys nothing here. Direct DOM writes are also what a prerenderer captures.
//
// IMPORTANT: this is client-side, so it only helps engines that execute
// JavaScript (Google, and Bing to a lesser degree). Yandex, Baidu, Naver,
// Seznam and DuckDuckGo largely do not - for those, the site has to be
// prerendered to static HTML at build time. See the notes in
// scripts/generate-sitemap.js.
function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return el;
}

export default function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    const priv = isPrivatePath(pathname);

    const title = meta?.title || `${SITE_NAME} | Watch New Releases in 200+ Countries`;
    const description =
      meta?.description ||
      'StarView Cinema streams new-release films in over 200 countries, with premium formats, real showtimes, gift cards and StarView X membership.';

    document.title = title;
    upsertMeta('meta[name="description"]', { name: 'description', content: description });

    // '/home' is the same page as '/', so it points at '/' rather than
    // competing with it.
    //
    // The trailing slash is not cosmetic: prerendered routes are written as
    // <path>/index.html, so Netlify serves "<path>/" and 301s the bare path
    // to it. A canonical naming the redirecting form points engines at a URL
    // that immediately moves.
    const base = pathname.toLowerCase() === '/home' ? '/' : pathname;
    const canonicalPath = base === '/' ? '/' : base.replace(/\/+$/, '') + '/';
    upsertLink('canonical', `${SITE_URL}${canonicalPath}`);

    // Gated and transactional pages are kept out of the index entirely -
    // they can only ever render a login wall to a crawler.
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: priv ? 'noindex,nofollow' : 'index,follow,max-image-preview:large',
    });

    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: `${SITE_URL}${canonicalPath}` });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
  }, [pathname]);

  return null;
}
